import { useEffect, useMemo, useState } from "react";
import { useOpenShift } from "../../api/queries";
import {
  getAutoSyncScheduler,
  getAutoSyncStatus,
  requestAutoSync,
  subscribeAutoSyncStatus,
  type AutoSyncScope,
  type AutoSyncStatus,
} from "./auto-sync";
import { AUTO_SYNC } from "./offline-sync-policy";

type UseBackgroundOfflineSyncOptions = {
  enabled?: boolean;
};

/**
 * Keeps the global auto-sync scheduler scoped to the active shift and triggers
 * periodic / connectivity / visibility drains while the POS shell is mounted.
 */
export function useBackgroundOfflineSync(
  branchId: string | null | undefined,
  deviceId: string | null | undefined,
  options: UseBackgroundOfflineSyncOptions = {},
): AutoSyncStatus {
  const hookEnabled = options.enabled ?? true;
  const { data: openShift } = useOpenShift(
    hookEnabled ? (branchId ?? null) : null,
    hookEnabled ? (deviceId ?? null) : null,
  );

  const scope = useMemo((): AutoSyncScope | null => {
    if (!hookEnabled || !branchId || !deviceId || !openShift?.id) {
      return null;
    }
    if (openShift.status !== "OPEN") return null;
    return {
      branchId,
      deviceId,
      shiftId: openShift.id,
    };
  }, [hookEnabled, branchId, deviceId, openShift?.id, openShift?.status]);

  const [status, setStatus] = useState<AutoSyncStatus>(getAutoSyncStatus);

  useEffect(() => subscribeAutoSyncStatus(setStatus), []);

  useEffect(() => {
    if (!scope) return;
    getAutoSyncScheduler().setScope(scope);
  }, [scope]);

  useEffect(() => {
    if (!scope || !AUTO_SYNC.enabled) return;

    const tick = () => {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      requestAutoSync("interval", scope);
    };

    tick();
    const intervalId = setInterval(tick, AUTO_SYNC.intervalMs);
    return () => clearInterval(intervalId);
  }, [scope]);

  useEffect(() => {
    if (!scope || !AUTO_SYNC.enabled || !AUTO_SYNC.drainOnOnline) return;

    const onOnline = () => requestAutoSync("online", scope);
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [scope]);

  useEffect(() => {
    if (!scope || !AUTO_SYNC.enabled) return;

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        requestAutoSync("visibility", scope);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () =>
      document.removeEventListener("visibilitychange", onVisibility);
  }, [scope]);

  return status;
}
